import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldAlert, Trash2, Flame, UserCheck, Truck, AlertTriangle, Plus, Check, X,
  ChevronRight, Clock, TrendingDown,
} from "lucide-react";
import api from "../lib/api";
import { Card, Badge, Button, Modal, PageHeader, PageLoader } from "../lib/ui";
import { fmtRp } from "../lib/constants";
import { useToast } from "../lib/toast";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { PhotoEvidence } from "../components/PhotoEvidence";
import { compressImage } from "../lib/image";

const ICONS = { Trash2, Flame, UserCheck, Truck, AlertTriangle, ShieldAlert };
const PLATFORMS = ["GoFood", "Grab", "ShopeeFood", "Internal"];
const COLOR = {
  red: "bg-red-50 text-red-600", orange: "bg-orange-50 text-orange-600",
  violet: "bg-violet-50 text-violet-600", blue: "bg-blue-50 text-blue-600",
  amber: "bg-amber-50 text-amber-600",
};

function ReportModal({ type, branches, menus, isBranchScoped, onClose, onDone }) {
  const { t } = useT();
  const toast = useToast();
  const beh = type.behavior || {};
  const fields = type.fields || [];
  const [form, setForm] = useState({ quantity: 1, refund_amount: 0, remake_amount: 0, remake_qty: 0, estimated_loss: 0 });
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (beh.consumption_basis === "bom" && !form.menu_item_id) { toast.error(t("Select a menu item")); return; }
    if (!isBranchScoped && !form.branch_id) { toast.error(t("Select a branch")); return; }
    if (photos.length === 0) { toast.error(t("Photo evidence is required (min 1)")); return; }
    setSaving(true);
    try {
      await api.post("/incidents", { type_key: type.key, ...form, photos });
      toast.success(t("Incident recorded"));
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={`${t("Report")}: ${type.name_id || type.name}`}
      footer={<>
        <Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button>
        <Button onClick={submit} loading={saving} disabled={photos.length === 0} data-testid="incident-submit">{t("Submit")}</Button>
      </>}>
      <div className="space-y-3">
        {!isBranchScoped && (
          <div>
            <label className="mb-1 block text-xs font-bold text-ink-soft">{t("Branch")}</label>
            <select value={form.branch_id || ""} onChange={(e) => set("branch_id", e.target.value)} data-testid="incident-branch"
              className="w-full rounded-xl border border-line p-2.5 text-sm">
              <option value="">{t("Select a branch")}</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        )}

        <div>
          <label className="mb-1 block text-xs font-bold text-ink-soft">{t("Reason")}</label>
          <select value={form.category || ""} onChange={(e) => set("category", e.target.value)} data-testid="incident-reason"
            className="w-full rounded-xl border border-line p-2.5 text-sm">
            <option value="">{t("Select reason")}</option>
            {(type.reasons || []).map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        {beh.consumption_basis === "bom" && (
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className="mb-1 block text-xs font-bold text-ink-soft">{t("Menu Item")}</label>
              <select value={form.menu_item_id || ""} onChange={(e) => set("menu_item_id", e.target.value)} data-testid="incident-menu"
                className="w-full rounded-xl border border-line p-2.5 text-sm">
                <option value="">{t("Select menu")}</option>
                {menus.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-ink-soft">{t("Qty")}</label>
              <input type="number" min="1" value={form.quantity} onChange={(e) => set("quantity", Number(e.target.value))} data-testid="incident-qty"
                className="w-full rounded-xl border border-line p-2.5 text-sm" />
            </div>
          </div>
        )}

        {fields.includes("platform") && (
          <div>
            <label className="mb-1 block text-xs font-bold text-ink-soft">{t("Platform")}</label>
            <select value={form.platform || ""} onChange={(e) => set("platform", e.target.value)} data-testid="incident-platform"
              className="w-full rounded-xl border border-line p-2.5 text-sm">
              <option value="">{t("Select platform")}</option>
              {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        )}
        {(fields.includes("refund_amount") || fields.includes("remake_amount")) && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mb-1 block text-xs font-bold text-ink-soft">{t("Refund Amount")}</label>
              <input type="number" min="0" value={form.refund_amount} onChange={(e) => set("refund_amount", Number(e.target.value))} data-testid="incident-refund"
                className="w-full rounded-xl border border-line p-2.5 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-ink-soft">{t("Remake Amount")}</label>
              <input type="number" min="0" value={form.remake_amount} onChange={(e) => set("remake_amount", Number(e.target.value))} data-testid="incident-remake-amt"
                className="w-full rounded-xl border border-line p-2.5 text-sm" />
            </div>
          </div>
        )}
        {fields.includes("delivery_ref") && (
          <div>
            <label className="mb-1 block text-xs font-bold text-ink-soft">{t("Delivery / Order Ref")}</label>
            <input value={form.delivery_ref || ""} onChange={(e) => set("delivery_ref", e.target.value)} data-testid="incident-ref"
              className="w-full rounded-xl border border-line p-2.5 text-sm" placeholder="GF-123" />
          </div>
        )}

        <div>
          <label className="mb-1 block text-xs font-bold text-ink-soft">{t("Note")}</label>
          <textarea rows={2} value={form.note || ""} onChange={(e) => set("note", e.target.value)} data-testid="incident-note"
            className="w-full rounded-xl border border-line p-2.5 text-sm" />
        </div>

        <div>
          <label className="mb-1 block text-xs font-bold text-ink-soft">{t("Estimated Loss Value")} (Rp)</label>
          <input type="number" min="0" value={form.estimated_loss} onChange={(e) => set("estimated_loss", Number(e.target.value))} data-testid="incident-estimated-loss"
            className="w-full rounded-xl border border-line p-2.5 text-sm" placeholder="0" />
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
          <PhotoEvidence
            tokens={photos} setTokens={setPhotos}
            uploadFile={(file) => compressImage(file, 1024, 0.7)}
            max={6} testid="incident-photos"
            label={`${t("Photo Evidence")} (${t("required")})`}
            hint={t("Attach at least 1 photo as evidence")}
          />
        </div>
      </div>
    </Modal>
  );
}

export default function LossIncident() {
  const { t } = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { isRole, hasPermission, scopeType } = useAuth();
  const [types, setTypes] = useState(null);
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState([]);
  const [branches, setBranches] = useState([]);
  const [menus, setMenus] = useState([]);
  const [reportType, setReportType] = useState(null);
  const isBranchScoped = scopeType === "branch";
  const canApprove = hasPermission("manage_incident");
  const pending = list.filter((i) => ["PENDING", "UNDER_REVIEW"].includes(i.status));
  const typeName = useCallback((i) => (types || []).find((t) => t.key === i.type_key)?.name_id || i.type_name, [types]);

  const load = useCallback(async () => {
    const [tp, sm, ls] = await Promise.all([
      api.get("/incidents/types"), api.get("/incidents/summary"), api.get("/incidents"),
    ]);
    setTypes(tp.data); setSummary(sm.data); setList(ls.data);
  }, []);

  useEffect(() => {
    load();
    api.get("/branches").then(({ data }) => setBranches(data)).catch(() => {});
    api.get("/menu-items").then(({ data }) => setMenus(data)).catch(() => {});
  }, [load]);

  const decide = async (id, action) => {
    try {
      await api.post(`/incidents/${id}/${action}`, {});
      toast.success(action === "approve" ? t("Approved") : action === "reject" ? t("Rejected") : t("Under Review"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };

  const openType = (tp) => {
    if (tp.external) { navigate("/waste"); return; }   // Inventory Waste keeps its own engine
    setReportType(tp);
  };

  if (!types || !summary) return <PageLoader />;

  return (
    <div className="animate-fade-up pb-24 lg:pb-2" data-testid="loss-incident">
      <PageHeader icon={ShieldAlert} title={t("Loss & Incident Management")}
        subtitle={t("Complete visibility into every operational loss — not just damaged stock")} />

      {/* Owner summary */}
      <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Card className="flex items-center gap-3 p-4" data-testid="li-total-value">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-50 text-red-600"><TrendingDown size={20} /></span>
          <div><p className="text-xs font-bold text-ink-mute">{t("Total Loss Value")}</p><p className="font-heading text-xl font-extrabold text-ink">{fmtRp(summary.total_value)}</p></div>
        </Card>
        <Card className="flex items-center gap-3 p-4" data-testid="li-food-cost">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-50 text-orange-600"><Flame size={20} /></span>
          <div><p className="text-xs font-bold text-ink-mute">{t("Food Cost Impact")}</p><p className="font-heading text-xl font-extrabold text-ink">{fmtRp(summary.food_cost_value)}</p></div>
        </Card>
        <Card className="flex items-center gap-3 p-4" data-testid="li-count">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-600"><AlertTriangle size={20} /></span>
          <div><p className="text-xs font-bold text-ink-mute">{t("Total Incidents")}</p><p className="font-heading text-xl font-extrabold text-ink">{summary.total_count}</p></div>
        </Card>
        <Card className="flex items-center gap-3 p-4" data-testid="li-pending">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-50 text-amber-600"><Clock size={20} /></span>
          <div><p className="text-xs font-bold text-ink-mute">{t("Awaiting Approval")}</p><p className="font-heading text-xl font-extrabold text-ink">{pending.length}</p></div>
        </Card>
      </div>

      {/* Category cards — report entry points */}
      <h3 className="mb-2 mt-6 font-heading text-base font-extrabold text-ink">{t("Report an Incident")}</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {types.map((tp) => {
          const Icon = ICONS[tp.icon] || AlertTriangle;
          const cat = summary.categories.find((c) => c.key === tp.key) || { count: 0, value: 0 };
          return (
            <button key={tp.key} onClick={() => openType(tp)} data-testid={`li-type-${tp.key}`}
              className="flex items-center gap-3 rounded-2xl border border-line bg-white p-4 text-left transition hover:shadow-lg">
              <span className={`flex h-12 w-12 items-center justify-center rounded-2xl ${COLOR[tp.color] || COLOR.amber}`}><Icon size={22} /></span>
              <div className="min-w-0 flex-1">
                <p className="font-heading text-sm font-extrabold text-ink">{tp.name_id || tp.name}</p>
                <p className="text-xs text-ink-mute">{cat.count} {t("records")} · {fmtRp(cat.value)}</p>
              </div>
              {tp.external ? <ChevronRight size={18} className="text-ink-mute" /> : <Plus size={18} className="text-primary" />}
            </button>
          );
        })}
      </div>

      {/* Pending approvals */}
      {canApprove && pending.length > 0 && (
        <Card className="mt-6 overflow-hidden p-0" data-testid="li-approvals">
          <div className="flex items-center gap-2 border-b border-line px-5 py-4">
            <Clock size={18} className="text-warn-text" />
            <h3 className="font-heading text-base font-extrabold">{t("Awaiting Approval")}</h3>
            <Badge tone="warn" className="ml-auto">{pending.length}</Badge>
          </div>
          <div className="divide-y divide-line">
            {pending.map((i) => (
              <div key={i.id} className="flex flex-wrap items-center gap-3 px-5 py-3" data-testid={`li-pending-${i.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-ink">{typeName(i)} · <span className="text-ink-soft">{i.category}</span></p>
                  <p className="text-xs text-ink-mute">{i.branch_name} · {i.menu_name || ""} {i.quantity ? `×${i.quantity}` : ""} · {i.created_by} · {i.estimated_loss ? fmtRp(i.estimated_loss) : ""}</p>
                </div>
                <Badge tone={i.status === "UNDER_REVIEW" ? "info" : "warn"}>{i.status === "UNDER_REVIEW" ? t("Under Review") : t("Open")}</Badge>
                {i.status === "PENDING" && (
                  <Button size="sm" variant="ghost" onClick={() => decide(i.id, "review")} data-testid={`li-review-${i.id}`}><Clock size={14} /> {t("Review")}</Button>
                )}
                <Button size="sm" variant="outline" onClick={() => decide(i.id, "reject")} data-testid={`li-reject-${i.id}`}><X size={14} /> {t("Return")}</Button>
                <Button size="sm" onClick={() => decide(i.id, "approve")} data-testid={`li-approve-${i.id}`}><Check size={14} /> {t("Approve")}</Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Recent incidents */}
      <Card className="mt-6 overflow-hidden p-0" data-testid="li-recent">
        <div className="flex items-center gap-2 border-b border-line px-5 py-4">
          <h3 className="font-heading text-base font-extrabold">{t("Recent Incidents")}</h3>
        </div>
        {list.length === 0 ? <p className="px-5 py-8 text-center text-sm text-ink-mute">{t("No incidents recorded")}</p> : (
          <div className="divide-y divide-line">
            {list.slice(0, 30).map((i) => (
              <div key={i.id} className="flex items-center gap-3 px-5 py-3" data-testid={`li-item-${i.id}`}>
                <Badge tone={i.status === "APPROVED" ? "success" : i.status === "REJECTED" ? "danger" : i.status === "UNDER_REVIEW" ? "info" : "warn"}>{i.status === "PENDING" ? t("Open") : i.status === "UNDER_REVIEW" ? t("Under Review") : t(i.status)}</Badge>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-bold text-ink">{typeName(i)} · <span className="text-ink-soft">{i.category}</span></p>
                  <p className="text-xs text-ink-mute">{i.branch_name} · {(i.created_at || "").slice(0, 10)} · {i.created_by}</p>
                </div>
                <span className="font-heading text-sm font-extrabold text-danger-text">{i.value_impact ? `-${fmtRp(i.value_impact)}` : "—"}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {reportType && (
        <ReportModal type={reportType} branches={branches} menus={menus} isBranchScoped={isBranchScoped}
          onClose={() => setReportType(null)} onDone={() => { setReportType(null); load(); }} />
      )}
    </div>
  );
}
